import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import Logger from "../../../services/Logger";
import StatsWordWheelService from "../../../services/StatsWordWheelService";
import AdminWordWheelService from "../tpl/AdminWordWheelService";
import WordWheelSeasonStatsDialog from "./WordWheelSeasonStatsDialog";
import WordWheelSeasonPublishedSection from "./WordWheelSeasonPublishedSection";
import { WORD_WHEEL_DEFAULT_SEASON } from "../wordWheelLevelUtils";

const STATE_ORDER = ["DRAFT", "READY_TO_REVIEW", "REVIEWED", "PUBLISHED"];

const getStateCount = (byState, state) => (byState && byState[state]) ?? 0;

const formatPercent = (value) => `${Number(value ?? 0).toFixed(1)}%`;

const formatMinMax = (value, percent) => `${value} (${formatPercent(percent)})`;

export default function WordWheelPuzzleStatsPanel({ language }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [groups, setGroups] = useState([]);
    const [openSyncNamesDialog, setOpenSyncNamesDialog] = useState(false);
    const [syncNamesLoading, setSyncNamesLoading] = useState(false);
    const [syncNamesResult, setSyncNamesResult] = useState(null);
    const [openSyncCharactersDialog, setOpenSyncCharactersDialog] = useState(false);
    const [syncCharactersLoading, setSyncCharactersLoading] = useState(false);
    const [syncCharactersResult, setSyncCharactersResult] = useState(null);
    const [statsReload, setStatsReload] = useState(0);
    const [openRemoveSubsetsDialog, setOpenRemoveSubsetsDialog] = useState(false);
    const [removeSubsetsLoading, setRemoveSubsetsLoading] = useState(false);
    const [removeSubsetsResult, setRemoveSubsetsResult] = useState(null);
    const [openSeasonStatsDialog, setOpenSeasonStatsDialog] = useState(false);
    const [selectedSeason, setSelectedSeason] = useState(WORD_WHEEL_DEFAULT_SEASON);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await StatsWordWheelService.statsByCharacterCount(language);
                if (!cancelled) {
                    setGroups(Array.isArray(result?.groups) ? result.groups : []);
                }
            } catch (err) {
                Logger.error("Failed to load word wheel stats:", err);
                if (!cancelled) {
                    setError("Failed to load statistics.");
                    setGroups([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [language, statsReload]);

    const sumByState = useMemo(() => {
        const sums = STATE_ORDER.reduce((acc, state) => {
            acc[state] = 0;
            return acc;
        }, {});
        let totalPuzzles = 0;
        groups.forEach((group) => {
            STATE_ORDER.forEach((state) => {
                sums[state] += getStateCount(group.byState, state);
            });
            totalPuzzles += group.puzzleCount ?? 0;
        });
        return { byState: sums, totalPuzzles };
    }, [groups]);

    const wordStatsSummary = useMemo(() => {
        const allMins = [];
        const allMaxs = [];
        let weightedAvgSum = 0;
        let totalPuzzles = 0;
        groups.forEach((group) => {
            const ws = group.wordStats;
            if (!ws) return;
            allMins.push(ws.min);
            allMaxs.push(ws.max);
            weightedAvgSum += (ws.average ?? 0) * (group.puzzleCount ?? 0);
            totalPuzzles += group.puzzleCount ?? 0;
        });
        return {
            min: allMins.length ? Math.min(...allMins) : 0,
            max: allMaxs.length ? Math.max(...allMaxs) : 0,
            average: totalPuzzles > 0 ? Math.round((weightedAvgSum / totalPuzzles) * 10) / 10 : 0,
            puzzleSharePercent: 100,
        };
    }, [groups]);

    const handleSyncNamesClick = () => {
        setSyncNamesResult(null);
        setOpenSyncNamesDialog(true);
    };

    const handleSyncNamesClose = () => {
        setOpenSyncNamesDialog(false);
        setSyncNamesResult(null);
    };

    const handleSyncNamesConfirm = async () => {
        setSyncNamesLoading(true);
        setSyncNamesResult(null);
        try {
            const result = await AdminWordWheelService.syncNamesFromCreatedAt(language);
            setSyncNamesResult(result);
        } catch (err) {
            Logger.error("Failed to sync word wheel template names:", err);
            setSyncNamesResult({ error: err?.message || "Failed to update names." });
        } finally {
            setSyncNamesLoading(false);
        }
    };

    const handleSyncCharactersClick = () => {
        setSyncCharactersResult(null);
        setOpenSyncCharactersDialog(true);
    };

    const handleSyncCharactersClose = () => {
        setOpenSyncCharactersDialog(false);
        setSyncCharactersResult(null);
    };

    const handleSyncCharactersConfirm = async () => {
        setSyncCharactersLoading(true);
        setSyncCharactersResult(null);
        try {
            const result = await AdminWordWheelService.syncCharactersFromWords(language);
            setSyncCharactersResult(result);
            if (result?.code === "SUCCESS") {
                setStatsReload((value) => value + 1);
            }
        } catch (err) {
            Logger.error("Failed to sync word wheel template characters:", err);
            setSyncCharactersResult({ error: err?.message || "Failed to update characters." });
        } finally {
            setSyncCharactersLoading(false);
        }
    };

    const handleRemoveSubsetsClick = () => {
        setRemoveSubsetsResult(null);
        setOpenRemoveSubsetsDialog(true);
    };

    const handleRemoveSubsetsClose = () => {
        setOpenRemoveSubsetsDialog(false);
        setRemoveSubsetsResult(null);
    };

    const handleRemoveSubsetsRun = async () => {
        setRemoveSubsetsLoading(true);
        setRemoveSubsetsResult(null);
        try {
            const result = await AdminWordWheelService.removeSubsetDuplicates(language, false);
            setRemoveSubsetsResult(result);
            if (result?.code === "SUCCESS") {
                setStatsReload((value) => value + 1);
            }
        } catch (err) {
            Logger.error("Failed to remove subset duplicate templates:", err);
            setRemoveSubsetsResult({ error: err?.message || "Failed to remove subset duplicates." });
        } finally {
            setRemoveSubsetsLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, gap: 2, flexWrap: "wrap" }}>
                <Typography variant="body2" color="text.secondary">
                    Puzzle counts grouped by wheel character count ({language}).
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button variant="outlined" onClick={handleSyncNamesClick}>
                        Update names
                    </Button>
                    <Button variant="outlined" onClick={handleSyncCharactersClick}>
                        Update characters
                    </Button>
                    <Button variant="outlined" color="error" onClick={handleRemoveSubsetsClick}>
                        Remove subset duplicates
                    </Button>
                </Box>
            </Box>

            <WordWheelSeasonPublishedSection
                language={language}
                season={selectedSeason}
                onSeasonChange={setSelectedSeason}
                reloadKey={statsReload}
                onManageLevels={() => setOpenSeasonStatsDialog(true)}
            />

            <WordWheelSeasonStatsDialog
                open={openSeasonStatsDialog}
                onClose={() => setOpenSeasonStatsDialog(false)}
                language={language}
                season={selectedSeason}
                onLevelsAssigned={() => setStatsReload((value) => value + 1)}
            />

            <Dialog open={openSyncNamesDialog} onClose={handleSyncNamesClose} maxWidth="sm" fullWidth>
                <DialogTitle>Update template names</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Set each {language} word wheel template name to{" "}
                        <strong>WW-&#123;createdAt epoch seconds&#125;</strong> based on its created date.
                        Templates that already match are skipped.
                    </DialogContentText>
                    {syncNamesLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    {syncNamesResult?.error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {syncNamesResult.error}
                        </Alert>
                    )}
                    {syncNamesResult && !syncNamesResult.error && syncNamesResult.code === "SUCCESS" && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            Updated {syncNamesResult.updated ?? 0} of {syncNamesResult.total ?? 0} templates.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSyncNamesClose} disabled={syncNamesLoading}>
                        {syncNamesResult && !syncNamesResult.error ? "Close" : "Cancel"}
                    </Button>
                    {!syncNamesResult?.code && (
                        <Button
                            variant="contained"
                            onClick={handleSyncNamesConfirm}
                            disabled={syncNamesLoading}
                        >
                            Update
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={openSyncCharactersDialog} onClose={handleSyncCharactersClose} maxWidth="sm" fullWidth>
                <DialogTitle>Update character details</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Set each {language} word wheel template&apos;s{" "}
                        <strong>characterCount</strong> and <strong>characters</strong> from its{" "}
                        <strong>wordsInUse</strong> letter pool (e.g. {`["a", "r", "t"]`}).
                        Templates that already match are skipped.
                    </DialogContentText>
                    {syncCharactersLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    {syncCharactersResult?.error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {syncCharactersResult.error}
                        </Alert>
                    )}
                    {syncCharactersResult && !syncCharactersResult.error && syncCharactersResult.code === "SUCCESS" && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            Updated {syncCharactersResult.updated ?? 0} of {syncCharactersResult.total ?? 0} templates.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSyncCharactersClose} disabled={syncCharactersLoading}>
                        {syncCharactersResult && !syncCharactersResult.error ? "Close" : "Cancel"}
                    </Button>
                    {!syncCharactersResult?.code && (
                        <Button
                            variant="contained"
                            onClick={handleSyncCharactersConfirm}
                            disabled={syncCharactersLoading}
                        >
                            Update
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={openRemoveSubsetsDialog} onClose={removeSubsetsLoading ? undefined : handleRemoveSubsetsClose} maxWidth="sm" fullWidth>
                <DialogTitle>Remove subset duplicates</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Keeps the puzzle with the most words in each overlapping group. Deletes smaller puzzles whose
                        entire word list is already included in a larger {language} puzzle, plus exact duplicates.
                        Published puzzles with play history are skipped. Click Run to execute.
                    </DialogContentText>
                    {removeSubsetsLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    {removeSubsetsResult?.error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {removeSubsetsResult.error}
                        </Alert>
                    )}
                    {removeSubsetsResult && !removeSubsetsResult.error && removeSubsetsResult.code === "SUCCESS" && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            Deleted {removeSubsetsResult.deleted ?? 0} templates
                            ({removeSubsetsResult.kept ?? 0} kept, {removeSubsetsResult.skipped ?? 0} skipped).
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRemoveSubsetsClose} disabled={removeSubsetsLoading}>
                        {removeSubsetsResult && !removeSubsetsResult.error ? "Close" : "Cancel"}
                    </Button>
                    {!removeSubsetsResult?.code && (
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleRemoveSubsetsRun}
                            disabled={removeSubsetsLoading}
                            startIcon={removeSubsetsLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            Run
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!loading && !error && groups.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>No word wheel templates found for this language.</Alert>
            )}

            {!loading && groups.length > 0 && (
                <>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                                By state
                            </Typography>
                            <TableContainer sx={{ overflowX: "auto" }}>
                                <Table size="small" stickyHeader sx={{ minWidth: 520 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Characters</TableCell>
                                            {STATE_ORDER.map((state) => (
                                                <TableCell key={state} align="right" sx={{ fontWeight: 600 }}>
                                                    {state.replace(/_/g, " ")}
                                                </TableCell>
                                            ))}
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                Puzzles
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {groups.map((group) => (
                                            <TableRow key={group.characterCount}>
                                                <TableCell sx={{ fontWeight: 600 }}>
                                                    {group.characterCount}
                                                </TableCell>
                                                {STATE_ORDER.map((state) => {
                                                    const value = getStateCount(group.byState, state);
                                                    return (
                                                        <TableCell
                                                            key={state}
                                                            align="right"
                                                            sx={value === 0 ? { color: "error.main", fontWeight: 600 } : undefined}
                                                        >
                                                            {value}
                                                        </TableCell>
                                                    );
                                                })}
                                                <TableCell align="right">{(group.puzzleCount ?? 0).toLocaleString()}</TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Sum</TableCell>
                                            {STATE_ORDER.map((state) => (
                                                <TableCell key={state} align="right" sx={{ fontWeight: 600 }}>
                                                    {sumByState.byState[state]}
                                                </TableCell>
                                            ))}
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {sumByState.totalPuzzles.toLocaleString()}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                                Words per puzzle
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Min and max show how many puzzles in the group have that word count (percentage within the group).
                                Share is the group&apos;s share of all puzzles.
                            </Typography>
                            <TableContainer sx={{ overflowX: "auto" }}>
                                <Table size="small" stickyHeader sx={{ minWidth: 520 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Characters</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Min</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Average</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Max</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Share</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {groups.map((group) => {
                                            const ws = group.wordStats ?? {};
                                            return (
                                                <TableRow key={`words-${group.characterCount}`}>
                                                    <TableCell sx={{ fontWeight: 600 }}>
                                                        {group.characterCount}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMinMax(ws.min ?? 0, ws.minPercent)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {Number(ws.average ?? 0).toFixed(1)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMinMax(ws.max ?? 0, ws.maxPercent)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatPercent(group.puzzleSharePercent)}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Overall</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {wordStatsSummary.min}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {wordStatsSummary.average.toFixed(1)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {wordStatsSummary.max}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {formatPercent(wordStatsSummary.puzzleSharePercent)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </>
            )}
        </Box>
    );
}
